/**
 * @Description: 神策埋点工具 - 使用 sa-sdk-javascript
 */

import { getEnv } from '@/utils/index';
import { getBeijingTime } from '@/utils/sentryLogger';

let isInitialized = false;
let sensors = null; // 延迟加载 sensors 实例
const version = process.env.npm_package_version || require('../package.json').version;

// 动态加载神策 SDK（仅在客户端）
const loadSensors = async () => {
  if (typeof window === 'undefined') {
    return null;
  }
  
  if (sensors !== null) {
    return sensors;
  }
  
  try {
    // 动态导入，避免在服务端执行
    // 使用 es6 版本，支持 ES6 模块
    const sensorsModule = await import('sa-sdk-javascript/dist/web/sensorsdata.es6.js');
    sensors = sensorsModule.default || sensorsModule;
    return sensors;
  } catch (error) {
    console.error('加载神策 SDK 失败:', error);
    return null;
  }
};

/**
 * 初始化神策埋点
 * @param {Object} config - 神策配置对象
 * @param {Object} config.commonProperties - 公共属性
 * @param {Object} config.options - 其他配置选项
 */
export async function initSensors(config = {}) {
  // 防止重复初始化
  if (isInitialized) {
    console.warn('神策埋点已经初始化，跳过重复初始化');
    return;
  }

  if (typeof window === 'undefined') {
    return;
  }

  // 动态加载 SDK
  const sensorsInstance = await loadSensors();
  if (!sensorsInstance) {
    console.error('神策 SDK 加载失败');
    return;
  }

  try {
    const env = getEnv();
    const serverUrl = env === 'production' 
      ? 'https://sensorsapi.lianlianpay.com/sa?project=production' 
      : 'https://sensorsapi.lianlianpay.com/sa?project=default';
    
    // 初始化神策SDK
    sensorsInstance.init({
      server_url: serverUrl,
      // 是否开启全埋点
      is_track_single_page: true, // 单页面应用开启，自动监听 URL 变化触发 $pageview 事件
      is_track_page_leave: true, // 开启页面离开自动采集（$pageleave），包含停留时长等信息
      is_track_device_id: true, // 在事件属性中暴露 $device_id，方便排查同一设备多次支付行为
      // 是否开启自动采集
      use_client_time: true, // 使用客户端时间
      send_type: 'beacon', // 使用beacon方式发送，页面关闭时也能发送
      // 是否开启热力图
      heatmap: {
        clickmap: 'default', // 点击图，开启后自动采集 $WebClick 事件
        scroll_notice_map: 'not_collect', // 触达图，关闭后不会自动采集 $WebStay 事件
      },
      show_log: !(["production", "sandbox", "test", "dev"].includes(env)), // 是否开启日志打印
      // 其他配置
      ...config.options,
    });

    // 设置公共属性
    // 注意：platform 可能已被神策 SDK 自动添加，避免重复定义
    const h5OrWeb = window.localStorage?.getItem('isMobileView') === 'WEB' ? 'WEB' : 'H5';
    
    const commonProperties = {
      // platform: 'web', // 移除，避免与 SDK 自动添加的 platform 冲突
      clientType: h5OrWeb,
      environment: env,
      appName: 'celerpay-checkout-counter',
      appVersion: version || '1.0.0',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      ...config.commonProperties,
    };

    sensorsInstance.registerPage(commonProperties);

    // 启用自动采集，用于采集 $pageview 事件
    sensorsInstance.quick('autoTrack');

    isInitialized = true;
    console.log('神策埋点初始化成功', { env, serverUrl });
  } catch (error) {
    console.error('神策埋点初始化失败:', error);
  }
}

/**
 * 等待神策埋点初始化完成
 * @param {number} maxWaitTime - 最大等待时间（毫秒），默认3000ms
 * @param {number} checkInterval - 检查间隔（毫秒），默认100ms
 * @returns {Promise<boolean>} 是否初始化完成
 */
const waitForInitialization = async (maxWaitTime = 3000, checkInterval = 100) => {
  if (isInitialized) {
    return true;
  }
  
  const startTime = Date.now();
  return new Promise((resolve) => {
    const checkInitialization = () => {
      if (isInitialized) {
        resolve(true);
        return;
      }
      
      const elapsed = Date.now() - startTime;
      if (elapsed >= maxWaitTime) {
        resolve(false);
        return;
      }
      
      setTimeout(checkInitialization, checkInterval);
    };
    
    checkInitialization();
  });
};

/**
 * 发送自定义事件
 * @param {string} eventName - 事件名称
 * @param {Object} properties - 事件属性
 * @example
 * sensorsTrack('支付页面访问', { page: '支付页面' });
 */
export async function sensorsTrack(eventName, properties = {}) {
  if (typeof window === 'undefined') {
    return;
  }

  // 如果未初始化，等待初始化完成（最多等待3秒）
  if (!isInitialized) {
    const initialized = await waitForInitialization(3000, 100);
    if (!initialized) {
      console.warn('神策埋点未初始化，事件未发送:', eventName);
      return;
    }
  }

  try {
    const sensorsInstance = sensors || await loadSensors();
    if (!sensorsInstance) {
      return;
    }
    
    const eventProperties = {
      ...properties,
      requestTime: getBeijingTime(),
    };

    // 发送事件
    sensorsInstance.track(eventName, eventProperties);
    
    // 开发环境打印详细日志
    if (getEnv() !== 'production') {
      console.log('神策埋点:', eventName, eventProperties);
    }
    
    // 生产环境也打印关键信息（便于调试）
    console.log(`[神策埋点] 事件已发送: ${eventName}`, {
      tdsKey: properties.tdsKey || 'N/A',
      timestamp: new Date().toISOString(),
      eventProperties: eventProperties,
    });
    
    // 检查是否有发送队列（神策 SDK 可能有队列机制）
    if (sensorsInstance && typeof sensorsInstance.getQueue === 'function') {
      const queue = sensorsInstance.getQueue();
      console.log('[神策埋点] 当前队列长度:', queue?.length || 0);
    }
  } catch (error) {
    console.error('神策埋点发送失败:', error, { eventName, properties });
  }
}

/**
 * 设置用户属性
 * @param {Object} properties - 用户属性对象
 * @example
 * sensorsSetProfile({ user_id: '12345', user_name: '张三' });
 */
export async function sensorsSetProfile(properties = {}) {
  if (typeof window === 'undefined' || !isInitialized) {
    if (!isInitialized) {
      console.warn('神策埋点未初始化，用户属性未设置');
    }
    return;
  }

  try {
    const sensorsInstance = sensors || await loadSensors();
    if (!sensorsInstance) {
      return;
    }
    sensorsInstance.setProfile(properties);
  } catch (error) {
    console.error('神策设置用户属性失败:', error, properties);
  }
}

/**
 * 设置用户ID（登录时调用）
 * @param {string} userId - 用户ID
 * @example
 * sensorsLogin('user_12345');
 */
export async function sensorsLogin(userId) {
  if (typeof window === 'undefined' || !isInitialized) {
    if (!isInitialized) {
      console.warn('神策埋点未初始化，用户ID未设置');
    }
    return;
  }

  try {
    const sensorsInstance = sensors || await loadSensors();
    if (!sensorsInstance) {
      return;
    }
    sensorsInstance.login(userId);
  } catch (error) {
    console.error('神策设置用户ID失败:', error, userId);
  }
}

/**
 * 清除用户ID（登出时调用）
 */
export async function sensorsLogout() {
  if (typeof window === 'undefined' || !isInitialized) {
    return;
  }

  try {
    const sensorsInstance = sensors || await loadSensors();
    if (!sensorsInstance) {
      return;
    }
    sensorsInstance.logout();
  } catch (error) {
    console.error('神策清除用户ID失败:', error);
  }
}

/**
 * 同步发送埋点（用于页面卸载时）
 * 注意：此函数不等待初始化，直接尝试发送，适用于 beforeunload 等场景
 * 由于神策 SDK 已配置 send_type: 'beacon'，直接调用 track 方法会使用 sendBeacon 发送
 * @param {string} eventName - 事件名称
 * @param {Object} properties - 事件属性
 */
export function sensorsTrackSync(eventName, properties = {}) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    // 如果 sensors 实例已加载且已初始化，直接使用（同步调用）
    if (sensors && isInitialized) {
      sensors.track(eventName, {
        ...properties,
        requestTime: getBeijingTime(),
      });
      // 强制刷新发送队列（确保立即发送）
      if (typeof sensors.flush === 'function') {
        sensors.flush();
      }
      return;
    }
    
    // 如果未初始化，尝试从全局获取（神策 SDK 可能已初始化）
    if (window.sensors && typeof window.sensors.track === 'function') {
      window.sensors.track(eventName, properties);
      if (typeof window.sensors.flush === 'function') {
        window.sensors.flush();
      }
      return;
    }
    
    console.warn('神策埋点未初始化，同步埋点可能无法发送:', eventName);
  } catch (error) {
    console.error('同步埋点发送失败:', error, { eventName, properties });
  }
}

// 导出获取 sensors 实例的函数，供高级用法
export const getSensors = () => sensors;
